import { Combobox, Select } from "@narmi/design_system";
import { useMemo, useState } from "react";

// Past this many options the plain dropdown scrolls inside the card, so the list
// becomes searchable instead (FIs may configure hundreds of partner employers).
const SEARCHABLE_OPTION_THRESHOLD = 10;

interface EligibilityOption {
  id: string;
  value: string;
  label: string;
  display_order: number;
}

interface EligibilityOptionSelectProps {
  label: string;
  options: EligibilityOption[];
  selectedOptionId: string | null;
  onOptionSelect: (optionId: string | null) => void;
}

/**
 * The follow-up control for an eligibility criteria: a plain dropdown for short
 * option lists, a searchable combobox once the list outgrows the card.
 *
 * Only rendered while its criteria is selected, so unmounting clears any
 * half-typed search along with the criteria's answer.
 */
function EligibilityOptionSelect({
  label,
  options,
  selectedOptionId,
  onOptionSelect,
}: EligibilityOptionSelectProps) {
  const optionsByLabel = useMemo(() => {
    const byLabel = new Map<string, EligibilityOption>();
    for (const option of options) {
      if (!byLabel.has(option.label)) {
        byLabel.set(option.label, option);
      }
    }
    return byLabel;
  }, [options]);

  const selectedOptionLabel = options.find((option) => option.id === selectedOptionId)?.label ?? "";
  // While the applicant is typing, the input follows their text; otherwise it
  // follows the selection, so a restored application prefills the label.
  const [typedSearchValue, setTypedSearchValue] = useState<string | null>(null);

  if (options.length <= SEARCHABLE_OPTION_THRESHOLD) {
    return (
      <Select label={label} value={selectedOptionId ?? ""} onChange={onOptionSelect}>
        {options.map((option) => (
          <Select.Item key={option.id} value={option.id}>
            {option.label}
          </Select.Item>
        ))}
      </Select>
    );
  }

  // The combobox is keyed by label because that is what it displays, so typed
  // text only counts as an answer once it matches an option exactly.
  const handleSearchChange = (searchValue: string) => {
    setTypedSearchValue(searchValue);
    onOptionSelect(optionsByLabel.get(searchValue)?.id ?? null);
  };

  return (
    <Combobox
      label={label}
      inputValue={typedSearchValue ?? selectedOptionLabel}
      onInputChange={handleSearchChange}
      onChange={handleSearchChange}
    >
      {options.map((option) => (
        <Combobox.Item key={option.id} value={option.label} searchValue={option.label}>
          {option.label}
        </Combobox.Item>
      ))}
    </Combobox>
  );
}

export { EligibilityOptionSelect };
export type { EligibilityOption };
